import Link from "next/link";
import type { ComponentProps } from "react";
import React from "react";
import { ChatPageContainer } from "~/components/common/chat/ChatPageContainer";
import { VendorDealLayout } from "~/components/common/layouts/deals/VendorDealLayout";
import Loader from "~/components/common/parts/loading/Loading";
import { useIdQueryHandler } from "~/hooks/useIdQueryHandler";
import { useMessages } from "~/hooks/vendors/deals/useMessages";
import { useDeals } from "~/hooks/vendors/useDeals";
import { useSessions } from "~/hooks/vendors/useSessions";
import { getOriginVendor, isOriginVendor } from "~/models/DealVendorCompany";
import { allowMessageByVendorCompany } from "~/models/VendorSession";
import { PATH as DEALS_PATH } from "~/pages/vendors/deals";
import { PATH as DEAL_PATH } from "~/pages/vendors/deals/[id]";

export const Page: React.FC = () => {
	const id = useIdQueryHandler();
	const { data: session } = useSessions().index();
	const { data: deal, isLoading: isLoadingDeal } = useDeals().show(id);
	const {
		data: messages,
		mutate: mutateMessages,
		isLoading: isLoadingMessages
	} = useMessages().index(id);
	const handleMutate = React.useCallback(async () => {
		await mutateMessages?.();
	}, [mutateMessages]);

	const isOrigin = React.useMemo(() => {
		if (session === null || deal === null) {
			return false;
		}
		return isOriginVendor(deal, session.vendorCompany.id);
	}, [deal, session]);
	const showMessage = React.useMemo(() => {
		if (session === null || deal === null) {
			return false;
		}
		if (isOrigin) {
			return true;
		}
		const originVendor = getOriginVendor(deal);
		if (originVendor === null) {
			return false;
		}
		return allowMessageByVendorCompany(session, originVendor.id);
	}, [deal, isOrigin, session]);
	if (
		isLoadingDeal ||
		isLoadingMessages ||
		deal === null ||
		messages === null
	) {
		return <Loader isLoading />;
	}
	if (!showMessage) {
		return null;
	}
	const items = [
		{ as: Link, href: DEALS_PATH, label: "案件一覧" },
		{ as: Link, href: DEAL_PATH(deal.id), label: "案件詳細" },
		{ label: "管理会社とチャット" }
	] as const satisfies ComponentProps<typeof VendorDealLayout>["items"];
	return (
		<VendorDealLayout deal={deal} items={items} useSimpleSpHeader>
			<ChatPageContainer
				deal={deal}
				handleMutate={handleMutate}
				messages={messages}
				receiver={isOrigin ? "vendor_company" : "partner_vendor_company"}
			/>
		</VendorDealLayout>
	);
};
